<script>
  import { onDestroy, onMount } from 'svelte';
  import { api } from '../lib/api.js';
  import { chatsStore, classifyChatError, defaultModelStore, getShowSystemPreference, loadModels, loadTemplates, modelsStore, setShowSystemPreference, showFlash, templatesStore } from '../lib/stores.js';
  import { TEXT } from '../lib/ui-text.js';
  import { groupModels, isModelBlocked, modelLabel } from '../lib/model-picker.js';
  import { api as templatesApi } from '../lib/api.js';
  import BottomSheet from '../lib/components/BottomSheet.svelte';
  import ActionSheet from '../lib/components/ActionSheet.svelte';

  export let chatId;

  let loading = true;
  let resetting = false;
  let error = null;
  let resetConfirmOpen = false;

  let systemPrompt = '';
  let model = '';
  let showSystemMessages = false;
  let responseMode = 'all';
  // Этап 25.3: «не отдавать российскому резерву» — показываем, только когда резерв настроен.
  let noDomesticFallback = false;
  let domesticFallbackAvailable = false;
  // Библиотека шаблонов (этап 24): пресет и след шаблона из настроек чата.
  let stageTemplate = 'project';
  let stageTemplateCustom = null;
  let templateName = null;
  let saveTemplateOpen = false;
  let saveTemplateName = '';
  let saveTemplateError = null;
  let templateBusy = false;
  let applySheetOpen = false;
  let pendingApply = null;

  // Режим ответа Петровича показываем только владельцу общего чата: в личном
  // чате он отвечает всегда, а участник настройки менять не может (403).
  $: chatMeta = $chatsStore.find((item) => item.chatId === chatId) || null;
  $: showResponseMode = Boolean(chatMeta?.isShared) && chatMeta?.isOwner !== false;
  // Дачному чату облачная цепочка не нужна — флажок там бессмыслен.
  $: showDomesticFlag = domesticFallbackAvailable && chatMeta?.isOwner !== false && chatMeta?.brain !== 'dacha' && chatMeta?.chatType !== 'dacha';
  // «Применить шаблон» — владельцу и не в дачном чате: промпт и пресет на дачу не действуют.
  $: canApplyTemplate = chatMeta?.isOwner !== false && chatMeta?.brain !== 'dacha' && chatMeta?.chatType !== 'dacha';
  $: modelGroups = groupModels($modelsStore);
  // Модель Yandex и флажок несовместимы (backend отвечает 409): при флажке модели
  // Yandex не выбираются, при модели Yandex флажок не ставится.
  $: domesticBlocked = showDomesticFlag && noDomesticFallback;
  $: selectedDomestic = Boolean($modelsStore.find((item) => item.id === model)?.domestic);
  $: applyActions = ($templatesStore || []).map((template) => ({
    id: `template:${template.templateId}`,
    label: template.name,
    icon: 'draft',
  }));

  async function loadSettings() {
    loading = true;
    try {
      // Всегда обновляем список моделей с бэка: whitelist мог измениться, а в живущей
      // PWA-сессии modelsStore иначе держит устаревший список (resume его не обновляет).
      await loadModels().catch((e) => console.warn('Failed to refresh models', e));
      const settings = await api.getChatSettings(chatId);
      model = settings.model || $defaultModelStore || '';
      systemPrompt = settings.systemPrompt || '';
      savedPrompt = systemPrompt;
      responseMode = settings.responseMode === 'mention' ? 'mention' : 'all';
      noDomesticFallback = Boolean(settings.noDomesticFallback);
      domesticFallbackAvailable = Boolean(settings.domesticFallbackAvailable);
      stageTemplate = settings.stageTemplate || 'project';
      stageTemplateCustom = settings.stageTemplateCustom || null;
      templateName = settings.templateName || null;
      loadTemplates();
      showSystemMessages = getShowSystemPreference(chatId);
      error = null;
    } catch (e) {
      error = classifyChatError(e, 'Не удалось загрузить настройки чата.');
    } finally {
      loading = false;
    }
  }

  // ── Автосохранение (В6, 1.27.6) ──
  // Кнопки «Сохранить» больше нет: «←» молча выбрасывал правки. Каждое поле уходит
  // отдельно (PUT принимает одно поле). Промпт — при уходе из поля или с экрана:
  // каждое его изменение пишет в историю системное сообщение (ТЗ §10.5), поэтому
  // сохранять его на каждой паузе при наборе нельзя. Сохранения идут по очереди.
  let saveState = 'idle'; // idle | saving | saved | error
  let savedTimer = null;
  let savedPrompt = '';
  let saveChain = Promise.resolve();

  function persist(patch) {
    saveState = 'saving';
    error = null;
    saveChain = saveChain.then(async () => {
      try {
        await api.updateChatSettings(chatId, patch);
        saveState = 'saved';
        clearTimeout(savedTimer);
        savedTimer = setTimeout(() => {
          if (saveState === 'saved') {
            saveState = 'idle';
          }
        }, 2000);
      } catch (e) {
        saveState = 'error';
        error = classifyChatError(e, TEXT.settings.saveFailed);
      }
    });
    return saveChain;
  }

  function savePromptIfChanged() {
    if (loading || systemPrompt === savedPrompt) {
      return saveChain;
    }
    const next = systemPrompt;
    return persist({ systemPrompt: next || null }).then(() => {
      if (saveState !== 'error') {
        savedPrompt = next;
      }
    });
  }

  function handleShowSystemChange() {
    // Настройка этого устройства — хранится локально, запрос не нужен.
    setShowSystemPreference(chatId, showSystemMessages);
  }

  async function resetContext() {
    resetConfirmOpen = false;
    resetting = true;
    error = null;
    try {
      await api.resetChatContext(chatId);
      showFlash(TEXT.settings.contextReset, 'info');
    } catch (e) {
      error = classifyChatError(e, TEXT.chat.resetContextFailed);
    } finally {
      resetting = false;
    }
  }

  async function goBack() {
    await savePromptIfChanged();
    if (saveState === 'error') {
      // Промпт не сохранился — остаёмся, чтобы его не потерять.
      return;
    }
    window.location.hash = `/chat/${chatId}`;
  }

  onDestroy(() => {
    clearTimeout(savedTimer);
    // Ушли с экрана не через «←» (свайп, ссылка) — промпт всё равно сохраняем.
    savePromptIfChanged();
  });

  // ── Библиотека шаблонов (этап 24) ──

  function openSaveTemplate() {
    saveTemplateName = chatMeta?.title || '';
    saveTemplateError = null;
    saveTemplateOpen = true;
  }

  async function saveAsTemplate() {
    const name = saveTemplateName.trim();
    if (!name || templateBusy) {
      return;
    }
    if (!systemPrompt.trim()) {
      saveTemplateError = TEXT.templates.failed;
      return;
    }
    templateBusy = true;
    saveTemplateError = null;
    try {
      await templatesApi.createTemplate({
        name,
        systemPrompt: systemPrompt.trim(),
        model: model || null,
        stageTemplate,
        stageTemplateCustom: stageTemplate === 'custom' ? stageTemplateCustom : null,
      });
      await loadTemplates();
      saveTemplateOpen = false;
      showFlash(TEXT.templates.saved, 'info');
    } catch (e) {
      saveTemplateError = e?.status === 409
        ? (/имен/i.test(e?.detail || '') ? TEXT.templates.nameTaken : TEXT.templates.limit)
        : classifyChatError(e, TEXT.templates.failed);
    } finally {
      templateBusy = false;
    }
  }

  function handleApplySelect(event) {
    applySheetOpen = false;
    const templateId = (event.detail.id || '').replace(/^template:/, '');
    const template = ($templatesStore || []).find((item) => item.templateId === templateId);
    if (!template) {
      return;
    }
    // Промпт чата непустой и отличается — подтверждение перед заменой.
    if (systemPrompt.trim() && systemPrompt.trim() !== template.systemPrompt.trim()) {
      pendingApply = template;
      return;
    }
    applyTemplate(template);
  }

  async function applyTemplate(template) {
    pendingApply = null;
    templateBusy = true;
    error = null;
    try {
      const payload = {
        systemPrompt: template.systemPrompt,
        stageTemplate: template.stageTemplate || 'project',
        stageTemplateCustom: template.stageTemplate === 'custom' ? template.stageTemplateCustom : null,
      };
      // Модель шаблона вне каталога (или Yandex при флажке) не передаём — остаётся модель чата.
      const templateModel = $modelsStore.find((item) => item.id === template.model);
      if (templateModel && !isModelBlocked(templateModel, domesticBlocked)) {
        payload.model = template.model;
      }
      await api.updateChatSettings(chatId, payload);
      systemPrompt = template.systemPrompt;
      savedPrompt = template.systemPrompt;
      stageTemplate = payload.stageTemplate;
      stageTemplateCustom = payload.stageTemplateCustom;
      if (payload.model) {
        model = payload.model;
      }
      showFlash(TEXT.templates.applied, 'info');
    } catch (e) {
      error = classifyChatError(e, TEXT.templates.failed);
    } finally {
      templateBusy = false;
    }
  }

  onMount(loadSettings);
</script>

<div class="settings-page">
  <header class="header">
    <button class="back-btn" on:click={goBack} aria-label="Назад">←</button>
    <h1>Настройки чата</h1>
    <span class="save-state" class:error={saveState === 'error'} aria-live="polite">
      {#if saveState === 'saving'}{TEXT.settings.saving}{:else if saveState === 'saved'}{TEXT.settings.saved}{/if}
    </span>
  </header>

  <div class="settings-content">
    {#if loading}
      <div class="loading-state">
        <div class="spinner"></div>
      </div>
    {:else}
      <div class="settings-form">
        <p class="hint autosave-hint">{TEXT.settings.autosaveHint}</p>
        <div class="form-group">
          <label for="model">Модель</label>
          <select id="model" bind:value={model} on:change={() => persist({ model })}>
            {#each modelGroups as group (group.provider)}
              <optgroup label={group.title}>
                {#each group.items as item (item.id)}
                  <option value={item.id} disabled={isModelBlocked(item, domesticBlocked) && item.id !== model}>{modelLabel(item.id)}</option>
                {/each}
              </optgroup>
            {/each}
          </select>
        </div>

        <div class="form-group">
          <label for="systemPrompt">Системный промпт</label>
          <textarea
            id="systemPrompt"
            bind:value={systemPrompt}
            on:blur={savePromptIfChanged}
            rows="6"
            placeholder="Задайте поведение ассистента..."
          ></textarea>
          <p class="hint">{TEXT.settings.promptHint}</p>
          {#if templateName}
            <p class="hint" data-testid="template-origin">{TEXT.templates.createdFrom.replace('{name}', templateName)}</p>
          {/if}
          <!-- Библиотека шаблонов (этап 24): сохранить настройки под именем или взять готовые. -->
          <div class="template-actions">
            <button type="button" class="btn-secondary" disabled={templateBusy} on:click={openSaveTemplate}>
              {TEXT.templates.saveAsTemplate}
            </button>
            {#if canApplyTemplate}
              <button type="button" class="btn-secondary" disabled={templateBusy} on:click={() => (applySheetOpen = true)}>
                {TEXT.templates.applyTemplate}
              </button>
            {/if}
          </div>
        </div>

        {#if showResponseMode}
          <fieldset class="form-group response-mode">
            <legend>{TEXT.family.responseModeLabel}</legend>
            <label class="checkbox-label">
              <input type="radio" name="responseMode" value="all" bind:group={responseMode} on:change={() => persist({ responseMode })} />
              <span>{TEXT.family.responseModeAll}</span>
            </label>
            <label class="checkbox-label">
              <input type="radio" name="responseMode" value="mention" bind:group={responseMode} on:change={() => persist({ responseMode })} />
              <span>{TEXT.family.responseModeMention}</span>
            </label>
            <p class="hint">{TEXT.family.responseModeHint}</p>
          </fieldset>
        {/if}

        {#if showDomesticFlag}
          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input type="checkbox" bind:checked={noDomesticFallback} disabled={selectedDomestic && !noDomesticFallback} on:change={() => persist({ noDomesticFallback })} />
              <span>{TEXT.brain.noDomesticLabel}</span>
            </label>
            <p class="hint">{selectedDomestic && !noDomesticFallback ? TEXT.brain.noDomesticNeedsModel : TEXT.brain.noDomesticHint}</p>
          </div>
        {/if}

        <div class="form-group checkbox-group">
          <label class="checkbox-label">
            <input type="checkbox" bind:checked={showSystemMessages} on:change={handleShowSystemChange} />
            <span>Показывать системные сообщения в чате и экспорте</span>
          </label>
          <p class="hint">
            Настройка хранится локально для этого чата и синхронизируется между вкладками.
          </p>
        </div>

        {#if error}
          <div class="error-message" role="alert">{error}</div>
        {/if}

        <div class="form-actions">
          <!-- Сброс — с подтверждением в шторке (1.27.6), а не системным диалогом. -->
          <button type="button" class="btn-warning" on:click={() => (resetConfirmOpen = true)} disabled={resetting}>
            {resetting ? 'Сброс...' : TEXT.chat.resetContextAction}
          </button>
        </div>
      </div>
    {/if}
  </div>
</div>

<BottomSheet open={saveTemplateOpen} title={TEXT.templates.saveAsTemplate} on:close={() => (saveTemplateOpen = false)}>
  <form class="template-form" data-testid="save-template" on:submit|preventDefault={saveAsTemplate}>
    <label class="hint" for="save-template-name">{TEXT.templates.nameLabel}</label>
    <input id="save-template-name" type="text" maxlength="60" placeholder={TEXT.templates.namePlaceholder} bind:value={saveTemplateName} disabled={templateBusy} />
    {#if saveTemplateError}
      <div class="error-message" role="alert">{saveTemplateError}</div>
    {/if}
    <div class="template-actions">
      <button type="button" class="btn-secondary" disabled={templateBusy} on:click={() => (saveTemplateOpen = false)}>{TEXT.templates.cancel}</button>
      <button type="submit" class="btn-primary" disabled={templateBusy || !saveTemplateName.trim()}>{TEXT.templates.save}</button>
    </div>
  </form>
</BottomSheet>

<ActionSheet
  open={applySheetOpen}
  title={TEXT.templates.applyTemplate}
  actions={applyActions.length ? applyActions : [{ id: 'none', label: TEXT.templates.noTemplates, disabled: true }]}
  on:select={handleApplySelect}
  on:close={() => (applySheetOpen = false)}
/>

<ActionSheet
  open={resetConfirmOpen}
  title={TEXT.chat.resetContextAction}
  actions={[{ id: 'reset', label: TEXT.chat.resetContextAction, danger: true, confirm: TEXT.chat.resetContextConfirm }]}
  on:select={resetContext}
  on:close={() => (resetConfirmOpen = false)}
/>

<ActionSheet
  open={pendingApply !== null}
  title={pendingApply?.name || ''}
  actions={[{ id: 'apply', label: TEXT.templates.applyTemplate, confirm: TEXT.templates.applyConfirm }]}
  on:select={() => applyTemplate(pendingApply)}
  on:close={() => (pendingApply = null)}
/>

<style>
  .save-state {
    margin-left: auto;
    font-size: 0.8125rem;
    color: var(--text-secondary);
  }

  .save-state.error {
    color: var(--error-text);
  }

  .autosave-hint {
    margin-bottom: var(--spacing-md);
  }

  .template-actions {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
    margin-top: 0.6rem;
  }

  .template-form {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0 0 var(--spacing-md);
  }

  .template-form input {
    width: 100%;
    box-sizing: border-box;
    font-size: 16px;
    padding: 0.5rem;
    border-radius: 8px;
    border: 1px solid var(--border-color);
    background: var(--bg-primary);
    color: var(--text-primary);
  }

  .settings-page {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  .header {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    padding: var(--spacing-md) var(--spacing-lg);
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
  }

  .back-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    /* В11 (1.27.2): цель касания 44px (HIG); отрицательный margin сохраняет плотность шапки. */
    width: 44px;
    height: 44px;
    margin: -6px;
    background: none;
    border: none;
    color: var(--text-primary);
    font-size: 1.25rem;
    border-radius: 8px;
    cursor: pointer;
  }

  .back-btn:hover {
    background: var(--bg-tertiary);
  }

  .header h1 {
    font-size: 1rem;
    font-weight: 600;
  }

  .settings-content {
    flex: 1;
    overflow-y: auto;
    padding: var(--spacing-lg);
    overscroll-behavior: contain;
  }

  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border-color);
    border-top-color: var(--accent-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  form {
    max-width: 600px;
    margin: 0 auto;
  }

  .form-group {
    margin-bottom: var(--spacing-lg);
  }

  label {
    display: block;
    margin-bottom: var(--spacing-sm);
    font-weight: 500;
    color: var(--text-primary);
  }

  textarea,
  select {
    width: 100%;
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }

  .hint {
    margin-top: var(--spacing-xs);
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .error-message {
    padding: 0.75rem;
    background: var(--error-bg);
    color: var(--error-text);
    border-radius: 8px;
    margin-bottom: var(--spacing-lg);
  }

  .response-mode {
    border: none;
    padding: 0;
    margin: 0 0 var(--spacing-lg);
  }

  .response-mode legend {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
    padding: 0;
  }

  .response-mode .checkbox-label {
    margin-bottom: 0.5rem;
  }

  .checkbox-group {
    padding: 1rem;
    border-radius: 10px;
    background: var(--bg-tertiary);
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0;
  }

  .checkbox-label input {
    width: auto;
  }


  .form-actions {
    display: flex;
    gap: var(--spacing-md);
    justify-content: flex-end;
    padding-top: var(--spacing-lg);
    border-top: 1px solid var(--border-color);
  }

  .btn-primary,
  .btn-secondary,
  .btn-warning {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all var(--transition-fast);
  }

  .btn-primary {
    background: var(--accent-color);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--accent-hover);
  }

  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }

  .btn-secondary:hover {
    background: var(--bg-elevated);
  }

  .btn-warning {
    background: rgba(245, 158, 11, 0.15);
    color: #b45309;
  }

  .btn-warning:hover:not(:disabled) {
    background: rgba(245, 158, 11, 0.22);
  }

</style>
